import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { TypesOfWork } from '$types/position/positionPropertyValues';
import { savePosition } from './mutations/save-position.mutation';
import {
  getAllPositions,
  getEmployeesByCompany,
  getPaginatedCompanies,
  getPApplications,
  getPositionFiltered,
  getPositionsBySkill,
  getPreviousApplications,
  loadCompanies,
  loadOnePosition,
  loadPositions,
  loadRoster,
} from './queries';
import {
  activatePosition,
  addCompanyMember,
  createCompany,
  deactivatePosition,
  deletePosition,
  duplicatePositionEmployer,
  duplicatePositionInternal,
  duplicatePositionWithCustomRoster,
  editPosition,
  removeCompanyMember,
  requestInterviews,
  trackingHiredCandidates,
  updateCompany,
} from './mutations';
import { editPositionInternal } from './mutations/edit-position-internal.mutation';
import { ReducerPaths } from '../../constants/reducerPaths';
import { updateToOfferSentConfirmation } from './mutations/update-to-offer-sent-confirmation.mutation';
import { rejectCandidateFromPositionEmployee } from './mutations/reject-candidate-from-position-employee.mutation';
import { CompaniesState } from './companies.types';
import PositionDto from '../../../types/serverTypes/positionDto';
import PositionStatus from '../../../constants/PositionStatus';
import { getTraceId } from '../../utils/traceId';
import { sessionHeaders } from '../../../utils/sessionIds';
import { withSessionRenewal } from '../../utils/baseQuery';
import { addNewPositionName } from './mutations/add-new-position-name.mutation';
import { getClientsByCompanyId } from './queries/get-clients-by-company-id.query';
import { savePositionEnhanced } from './mutations/save-position-enhanced.mutation';
import { getCompanyInfo } from './queries/get-company-info.query';
import { getCompanySettings } from './queries/get-company-settings.query';
import { updateCompanySettings } from './mutations/update-company-settings.mutation';
import { editPositionEnhanced } from './mutations/edit-position-enhanced.mutation';

const customParamsSerializer = (params: Record<string, any>) => {
  const serializedParams = Object.entries(params)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .map((v) => `${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`)
          .join('&');
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');

  return serializedParams;
};

export const companiesApi = createApi({
  reducerPath: ReducerPaths.COMPANIES,
  baseQuery: withSessionRenewal(
    fetchBaseQuery({
      baseUrl: `${import.meta.env.REACT_APP_API_URL}`,
      prepareHeaders: (headers) => {
        const token = localStorage.getItem('id_token');
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }

        const segmentTraceId = getTraceId();
        if (segmentTraceId) {
          headers.set('x-trace-id', segmentTraceId);
        }

        // The device and visit, so the request log can rebuild sessions (identity-and-funnel.md §8)
        Object.entries(sessionHeaders()).forEach(([name, value]) =>
          headers.set(name, value)
        );

        return headers;
      },
      paramsSerializer: customParamsSerializer,
    })
  ),
  keepUnusedDataFor: 240,
  refetchOnMountOrArgChange: 30,
  refetchOnReconnect: true,
  tagTypes: ['Company', 'PositionsList'],
  endpoints: (builder) => ({
    savePosition: savePosition(builder),
    editPosition: editPosition(builder),
    editPositionInternal: editPositionInternal(builder),
    loadPositions: loadPositions(builder),
    requestInterviews: requestInterviews(builder),
    loadCompanies: loadCompanies(builder),
    updateCompanies: updateCompany(builder),
    createCompany: createCompany(builder),
    addCompanyMember: addCompanyMember(builder),
    removeCompanyMember: removeCompanyMember(builder),
    getCompanySettings: getCompanySettings(builder),
    updateCompanySettings: updateCompanySettings(builder),
    loadOnePosition: loadOnePosition(builder),
    getEmployeesByCompany: getEmployeesByCompany(builder),
    trackingHiredCandidates: trackingHiredCandidates(builder),
    getCompaniesPaginated: getPaginatedCompanies(builder),
    activatePosition: activatePosition(builder),
    deactivatePosition: deactivatePosition(builder),
    deletePosition: deletePosition(builder),
    loadRoster: loadRoster(builder),
    duplicatePositionWithCustomRoster:
      duplicatePositionWithCustomRoster(builder),
    duplicatePositionInternal: duplicatePositionInternal(builder),
    duplicatePositionEmployer: duplicatePositionEmployer(builder),
    internalFilterPositions: getPositionFiltered(builder),
    updateToOfferSentConfirmation: updateToOfferSentConfirmation(builder),
    rejectCandidateFromPositionEmployee:
      rejectCandidateFromPositionEmployee(builder),
    getAllPositions: getAllPositions(builder),
    getPositionsBySkill: getPositionsBySkill(builder),
    getApplications: getPApplications(builder),
    getPreviousApplications: getPreviousApplications(builder),
    addNewPositionName: addNewPositionName(builder),
    getClientsByCompanyId: getClientsByCompanyId(builder),
    savePositionEnhanced: savePositionEnhanced(builder),
    getCompanyInfo: getCompanyInfo(builder),
    editPositionEnhanced: editPositionEnhanced(builder),
  }),
});

const initialState: CompaniesState = {
  activePosition: null,
  positions: [],
  employerLastCreatedPositionId: null,
  employerAddPositionForm: {
    SalaryUpperBound: 0,
    SalaryLowerBound: 0,
    jobTitle: '',
    JobPosition: null,
    jobIsntListed: false,
    IdealStartDate: '',
    TypeOfWork: TypesOfWork.FullTime,
    customJobType: '',
    file: null,
    RequiredSkills: [],
    NiceToHaveSkills: [],
    Responsibilities: '',
    SeniorityLevel: '',
    WorkingHours: '',
    WorkingDays: '',
    bonus: false,
    BonusDetails: '',
    commission: false,
    CommissionDetails: '',
    benefits: false,
    BenefitsDetails: '',
    other: false,
    OtherCompensation: '',
    Languages: [],
    Country: null,
    WorkLocation: null,
    JobDescription: '',
    DegreeType: null,
    Company: null,
    DealId: '',
    TenantId: -1,
    IsPublic: false,
    EnableSmartInterviews: null,
    SmartInterviewQuestionId: null,
    ShowInterestedToEmployer: false,
    FeeAmount: null,
  },
};

export const companySlice = createSlice({
  name: ReducerPaths.COMPANY,
  initialState,
  reducers: {
    setActivePosition: (state, action) => {
      state.activePosition = action.payload;
    },
    concatPositions: (state, action: PayloadAction<PositionDto[]>) => {
      // removes duplicates (after adding a new position, the new position goes to the first index so the other items are moved)
      state.positions = [...(state.positions as PositionDto[])]
        .concat(action.payload)
        .reduce((prev: PositionDto[], act: PositionDto) => {
          const alreadyExists = prev.some(
            (singlePos) => singlePos.Id === act.Id
          );
          if (!alreadyExists) prev.push(act);
          return prev;
        }, []);
    },
    setPositions: (state, action: PayloadAction<PositionDto[]>) => {
      state.positions = [...action.payload];
    },
    removePosition: (state, action: PayloadAction<number>) => {
      state.positions = state.positions.filter(
        (position) => position.Id !== action.payload
      );
    },
    deactivatePositionSlice: (state, action: PayloadAction<number>) => {
      state.positions = state.positions.map((position) =>
        position.Id === action.payload
          ? { ...position, Status: PositionStatus.Deactivated }
          : position
      );
    },
    activatePositionSlice: (state, action: PayloadAction<number>) => {
      state.positions = state.positions.map((position) =>
        position.Id === action.payload
          ? { ...position, Status: PositionStatus.Open }
          : position
      );
    },

    addPosition: (state, action: PayloadAction<PositionDto>) => {
      state.positions = [action.payload, ...state.positions];
    },
    updateStatePosition: (state, action: PayloadAction<PositionDto>) => {
      state.positions = state.positions.map((position) =>
        position.Id === action.payload.Id ? action.payload : position
      );
    },

    /* Employer Add Position */
    employerAddPositionOverview: (
      state,
      action: PayloadAction<CompaniesState['employerAddPositionForm']>
    ) => {
      state.employerAddPositionForm = {
        ...state.employerAddPositionForm,
        ...action.payload,
      };
    },
    employerSaveCreatedPositionId: (state, action: PayloadAction<number>) => {
      state.employerLastCreatedPositionId = action.payload;
    },
    resetEmployerAddPositionForm: (state) => {
      state.employerAddPositionForm = initialState.employerAddPositionForm;
    },
  },
});

export const {
  useSavePositionMutation,
  useEditPositionMutation,
  useEditPositionInternalMutation,
  useLoadPositionsQuery,
  useRequestInterviewsMutation,
  useLoadCompaniesQuery,
  useUpdateCompaniesMutation,
  useCreateCompanyMutation,
  useAddCompanyMemberMutation,
  useRemoveCompanyMemberMutation,
  useLoadOnePositionQuery,
  useLazyLoadOnePositionQuery,
  useGetEmployeesByCompanyQuery,
  useGetCompaniesPaginatedQuery,
  useActivatePositionMutation,
  useDeactivatePositionMutation,
  useDeletePositionMutation,
  useLoadRosterQuery,
  useDuplicatePositionWithCustomRosterMutation,
  useDuplicatePositionInternalMutation,
  useDuplicatePositionEmployerMutation,
  useInternalFilterPositionsQuery,
  useLazyInternalFilterPositionsQuery,
  useUpdateToOfferSentConfirmationMutation,
  useRejectCandidateFromPositionEmployeeMutation,
  useTrackingHiredCandidatesMutation,
  useGetAllPositionsQuery,
  useGetPositionsBySkillQuery,
  useGetApplicationsQuery,
  useGetPreviousApplicationsQuery,
  useAddNewPositionNameMutation,
  useGetClientsByCompanyIdQuery,
  useSavePositionEnhancedMutation,
  useGetCompanyInfoQuery,
  useGetCompanySettingsQuery,
  useUpdateCompanySettingsMutation,
  useEditPositionEnhancedMutation,
} = companiesApi;

export const {
  setActivePosition,
  addPosition,
  concatPositions,
  removePosition,
  updateStatePosition,
  setPositions,
  deactivatePositionSlice,
  activatePositionSlice,
} = companySlice.actions;
